import React from "react";
import styled from "styled-components";
import { LuArrowRight, LuCodeXml } from "react-icons/lu";
import teamOffice from "../assets/team-office.jpg";
import { ButtonLink, Container, Eyebrow, IconBox, Reveal } from "./ui";

const HeroSection = styled.section`
  padding: calc(var(--nav-height) + 80px) 0 104px;
  background: linear-gradient(
    180deg,
    var(--color-bg-tint) 0%,
    var(--color-bg) 85%
  );

  @media (max-width: 768px) {
    padding: calc(var(--nav-height) + 40px) 0 72px;
  }
`;

const Layout = styled(Container)`
  display: grid;
  grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr);
  align-items: center;
  gap: 72px;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    gap: 56px;
  }
`;

const Headline = styled(Reveal)`
  font-size: clamp(2.375rem, 4.6vw, 3.5rem);
  font-weight: 600;
  line-height: 1.06;
  letter-spacing: -0.03em;
  margin-bottom: 24px;

  span {
    color: var(--color-primary);
  }
`;

const Intro = styled.p`
  font-size: 1.1875rem;
  line-height: 1.7;
  color: var(--color-muted);
  max-width: 560px;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    font-size: 1.0625rem;
  }
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;

  @media (max-width: 480px) {
    a {
      flex: 1 1 100%;
    }
  }
`;

const Stats = styled.dl`
  display: grid;
  grid-template-columns: repeat(3, auto);
  justify-content: start;
  gap: 48px;
  margin-top: 56px;
  padding-top: 32px;
  border-top: 1px solid var(--color-border);

  @media (max-width: 480px) {
    gap: 24px;
    grid-template-columns: repeat(3, 1fr);
  }
`;

// column-reverse shows the number above its label while keeping dt before dd
// in the markup; flex-end pins both to the top so the numbers align.
const Stat = styled.div`
  display: flex;
  flex-direction: column-reverse;
  justify-content: flex-end;
  gap: 4px;

  dt {
    font-size: 0.875rem;
    color: var(--color-muted);
  }

  dd {
    font-family: var(--font-heading);
    font-size: 2rem;
    font-weight: 600;
    line-height: 1;
    letter-spacing: -0.02em;
    color: var(--color-ink);
  }
`;

const Visual = styled(Reveal)`
  position: relative;
  padding-bottom: 32px;

  @media (max-width: 960px) {
    max-width: 560px;
  }
`;

const Photo = styled.img`
  width: 100%;
  height: auto;
  aspect-ratio: 1 / 1;
  object-fit: cover;
  border-radius: 20px;
  border: 1px solid var(--color-border);

  @media (max-width: 960px) {
    aspect-ratio: 4 / 3;
  }
`;

const DeliveryCard = styled.div`
  position: absolute;
  left: -32px;
  bottom: 0;
  display: flex;
  align-items: center;
  gap: 14px;
  max-width: 340px;
  padding: 16px 20px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 14px;
  box-shadow: var(--shadow-lg);

  strong {
    display: block;
    font-family: var(--font-heading);
    font-size: 1rem;
    font-weight: 600;
    color: var(--color-ink);
  }

  span {
    font-size: 0.875rem;
    color: var(--color-muted);
  }

  @media (max-width: 960px) {
    left: 16px;
    right: 16px;
  }
`;

const stats = [
  { value: "5+", label: "Years of experience" },
  { value: "49+", label: "Projects delivered" },
  { value: "30+", label: "Clients served" },
];

const Hero = () => (
  <HeroSection id="hero">
    <Layout>
      <div>
        <Reveal>
          <Eyebrow>Software studio · Rabat, Morocco</Eyebrow>
        </Reveal>

        <Headline forwardedAs="h1" delay={0.05}>
          We engineer <span>web, mobile &amp; AI</span> products that
          businesses rely on.
        </Headline>

        <Reveal delay={0.1}>
          <Intro>
            Code Solutions is a full-stack team building web platforms, mobile
            apps, AI systems and IoT solutions. Whether you're a startup or an
            established business, we take your idea from concept to a fast,
            secure and scalable product.
          </Intro>
        </Reveal>

        <Reveal delay={0.15}>
          <Actions>
            <ButtonLink href="#contact">
              Start a project
              <LuArrowRight data-arrow aria-hidden="true" />
            </ButtonLink>
            <ButtonLink href="#portfolio" $variant="secondary">
              View our work
            </ButtonLink>
          </Actions>
        </Reveal>

        <Reveal delay={0.2}>
          <Stats>
            {stats.map(stat => (
              <Stat key={stat.label}>
                <dt>{stat.label}</dt>
                <dd>{stat.value}</dd>
              </Stat>
            ))}
          </Stats>
        </Reveal>
      </div>

      <Visual delay={0.1}>
        <Photo
          src={teamOffice}
          alt="The Code Solutions team in a project workshop"
          width="1024"
          height="1024"
          fetchpriority="high"
        />
        <DeliveryCard>
          <IconBox>
            <LuCodeXml aria-hidden="true" />
          </IconBox>
          <div>
            <strong>End-to-end delivery</strong>
            <span>Concept · Design · Development · Deployment</span>
          </div>
        </DeliveryCard>
      </Visual>
    </Layout>
  </HeroSection>
);

export default Hero;
